import { queryOptions } from '@tanstack/react-query';

import {
  apiGet,
  apiPatch,
  apiPost,
  type ApiError,
  type BodyOf,
  type QueryOf,
  type ResponseOf,
} from './http';
import { queryKeys } from './queryKeys';
import type { components } from './schema';

/**
 * The interview surface — §6.6 (filling in), §6.7 (submitted) and the scheduling act, which had no
 * screen anywhere until TASK-081 put it on §6.5's consideration block — as six operations over the one
 * `apiClient`, and **one place where ADR 0006's two response shapes are told apart**.
 *
 * ⚠️ **Feedback protection #3 — others' feedback is hidden until yours is submitted — is a *type* in
 * this module and a rendering convention nowhere.** `.claude/rules/frontend.md` writes the rule as
 * *"the data must not reach the client at all"*, and the server already keeps it: `visible_interviews`
 * selects the two text columns as `CASE WHEN <gate> THEN <column> END`, so a blinded row's feedback is
 * never read from the database, the row **fails to validate** as `InterviewOut` and falls through to
 * `InterviewMetadataOut` — metadata and nothing else
 * (`backend/sprints/sprint-04-the-interview-DONE.md`, TASK-030). The client's entire job on this side is
 * to never undo that, and the only way it could is a cast. So there is exactly one discriminator here
 * (`isReadable`), it decides on the presence of a key the blinded shape cannot carry, and
 * `interviews.test.tsx` walks every source file to assert that no module asserts an interview payload
 * into the readable shape.
 *
 * Three more things are worth knowing before adding to it:
 *
 *  * **Two viewers legitimately receive different bodies for one URL**, which is why every key here
 *    carries the viewer (`queryKeys.interviews`, `queryKeys.interview`) and why nothing may cache this
 *    payload on the URL alone — `InterviewOut`'s own docstring says the same to the server side.
 *  * **`submit` takes no body, and no type can hold it to that.** `BodyOf` cannot refuse a body on a
 *    bodiless operation (an optional `requestBody` in the conditional makes `B` infer `unknown` —
 *    TASK-075), and this is the product's one irreversible act, so the guard is a test on the recorded
 *    request and there is no second one.
 *  * **Nothing here catches an `ApiError`.** A 401 signs the app out (`queryClient.ts`), a 429 reads as
 *    a throttle, and a 404 on a record of this surface is *"outside what you may see"* rather than *"no
 *    such thing"* — `CANDIDATE_SCOPED_RECORD` in `http.ts` already covers `/api/v1/interviews/\d+`,
 *    which is what makes §8's Forbidden state the right answer for it (TASK-080).
 */

/** `?consideration_id=`, `?person_id=`, `?submitted=`, `?page=`, `?page_size=`, read off the schema. */
export type InterviewListQuery = NonNullable<QueryOf<'/api/v1/interviews/', 'get'>>;

/** `InterviewRead` — the document's union, exactly as the two reads answer with it. */
type InterviewReadFromApi = ResponseOf<'/api/v1/interviews/{interview_id}/', 'get'>;

/** `InterviewOut` — the readable member, before this layer narrows its verdict. */
type InterviewOutFromApi = Extract<InterviewReadFromApi, { scores: unknown }>;

type InterviewPageFromApi = ResponseOf<'/api/v1/interviews/', 'get'>;

/**
 * **The blinded shape — `InterviewMetadataOut`, derived from the document and not restated.**
 *
 * It says *that* a conversation exists, **who** owes the write-up and whether they have — and nothing
 * whatever about the feedback. There is deliberately **no `person_id` and no `role_title`** on it:
 * TASK-080 designs a state for that and may not invent either (adding `person_id` would newly disclose
 * *which candidate* a hidden conversation is about to somebody who typed an id, which this shape omits on
 * purpose).
 *
 * ⚠️ **Its key set is not counted here, and that is BUG-036's lesson rather than tidiness.** This
 * docblock enumerated *"five keys"* — wording now retracted — for two sprints after ADR 0016 admitted
 * `interviewer_name` (2026-07-30): the count was restated in eight places in this client, in two
 * documents and in the
 * OpenAPI document itself, so a reader of any of them concluded the shape omitted the very field two
 * screens were already rendering. The key set lives in one place, `InterviewMetadataOut`'s own properties
 * in `openapi.snapshot.json`, and `interviews.test.tsx` asserts a blinded payload's keys **equal** them:
 * a seventh key fails the client, in the client, rather than only the server.
 */
export type InterviewMetadataOut = Exclude<InterviewReadFromApi, { scores: unknown }>;

/**
 * **The readable shape**, as the document declares it — **no narrowing since backend TASK-104.**
 *
 * ⚠️ **This was `Omit<…> & { overall_recommendation: Recommendation | null }`, and the reason was real
 * while it lasted**: the document typed the field `string`, a draft's verdict is `''` (the column is
 * `blank=True` with no default precisely so an undecided interviewer can still save — ADR 0007), and a
 * value outside the four reaching `StatusDot` makes `styles[value]` `undefined`, where reading
 * `style.pill` throws and takes the screen with it. So this layer narrowed at the edge.
 *
 * The API now answers one of the four **or `null`** in every payload that carries a verdict, and the
 * empty string never leaves the database — so the generated type says exactly this, and the alias is
 * the document's own shape. The guarantee that made the narrowing necessary is now server-side and
 * asserted there (`tests/test_recommendation_typing.py`), which is where it belonged: this client was
 * the only reader that had it.
 */
export type InterviewOut = InterviewOutFromApi;

/**
 * **ADR 0006's union, and the only interview type a screen ever holds.**
 *
 * One id, two shapes, decided by the caller's own reach — never by a flag and never by a status code
 * (`GET /api/v1/interviews/{id}/` is 200 either way; a 404 means the interview is outside this caller's
 * reach altogether). Narrow it with `isReadable` and nothing else.
 */
export type InterviewRead = InterviewOut | InterviewMetadataOut;

/** `PagedInterviewRead` — **two shapes in one page, decided per row.** */
export type InterviewPage = Omit<InterviewPageFromApi, 'items'> & { items: InterviewRead[] };

/**
 * `ScoreOut` — one question's assessment, in **frozen bank order**: the wording as it was asked
 * (`question_text_snapshot`, ADR 0004 — never the live wording), `question_has_been_edited` so §6.7's
 * *"This question has since been edited."* costs no second request, the rating and the comment.
 */
export type Score = InterviewOutFromApi['scores'][number];

/** `AddendumOut` — id, author, timestamp, text. Written once, and immutable (ADR 0008). */
export type Addendum = InterviewOutFromApi['addenda'][number];

/**
 * ⚠️ **The one discrimination in the client, and the whole of protection #3 on this side.**
 *
 * It decides on **`scores`** — a key the blinded shape cannot carry, because `InterviewMetadataOut` is a
 * separate class rather than `InterviewOut` with some keys nulled, and *"the key set is the contract"*
 * (its own docstring). Any of the six keys the readable shape adds would serve; this is the one whose
 * absence *is* the protection, so a reader of the branch is looking at the right fact.
 *
 * It is generic over the two flavours of the union this module holds — the payload as it arrives and the
 * payload with its verdict narrowed — so `'scores' in` appears **once** in the whole client rather than
 * once per shape. The constraint is `InterviewMetadataOut`, which both members satisfy: anything that
 * says at least *that* a conversation exists can be asked this question, and nothing else can.
 *
 * The negative branch is the load-bearing half: it narrows to `InterviewMetadataOut`, so reading
 * `scores`, `overall_comment`, `overall_recommendation` or `addenda` off it is a **`tsc` error**, pinned
 * by four `@ts-expect-error` lines in `interviews.test.tsx` that fail the lint gate the day any of them
 * stops being an error.
 */
export function isReadable<T extends InterviewMetadataOut>(
  interview: T,
): interview is Extract<T, { scores: unknown }> {
  return 'scores' in interview;
}

/**
 * The reachable interviews, in the shape ADR 0006 allows each of them.
 *
 * **All three groups may call this and the status code is the same for all three** — what differs is the
 * queryset: `Route("get", INTERVIEWS, None, (401, 200, 200, 200, 403, 403))` in
 * `backend/tests/test_permission_matrix.py`, actors `(anonymous, Interviewer, Recruiter, Admin,
 * superuser, superuser-in-Admin)`. The three narrowing parameters can never widen the reachable set —
 * an id from another tenant matches nothing and the page is empty, never a 403 — and `count` is the
 * number of **reachable** rows, blinded ones included, which is what makes §6.3's *"submitted over
 * total"* denominator buildable at all.
 */
export async function fetchInterviews(query: InterviewListQuery = {}): Promise<InterviewPage> {
  // Handed on exactly as sent: this layer narrowed each row's verdict until backend TASK-104 and now
  // adds nothing to either shape — no key, no invented `person_id`, no verdict filled in where the
  // blinded shape has no verdict at all. That is what lets TASK-080 assert a blinded interview's
  // rendered text against the six keys it really has.
  return apiGet('/api/v1/interviews/', { query });
}

/**
 * ⚠️ **The key carries the viewer *and* the query, and until TASK-079 it carried only the viewer** —
 * `['interviews', viewerId]`, so `?person_id=1` and `?person_id=2` were one entry and §6.7's *Other
 * interviews* block would have rendered one candidate's conversations under another's id. The full
 * argument is in `queryKeys.interviews`; what belongs here is that the query reaching the key and the
 * query reaching the URL are the **same object**, so they cannot drift.
 *
 * ⚠️ **No `placeholderData`, no `keepPreviousData` and no `select` on this or on `interviewQuery`
 * below.** Sprint 8's TASK-058 shipped `keepPreviousData` on a correctly keyed query and it resolved
 * from the **observer's** last payload under *any* key while reporting `status: 'success'` — so on this
 * surface a blinded interview would render the previously readable one, which is the exact walk-around
 * of a correct cache key and would defeat protection #3 with every backend test green.
 */
export function interviewsQuery(viewerId: number, query: InterviewListQuery = {}) {
  /*
   * The error type is named for the reason `considerationsQuery` names it: every failure of this route is
   * an `ApiError` (`apiGet` converts anything else before it leaves `http.ts`) and `ApiErrorState` is
   * what decides which §8 state a failure is. Without it a screen would hold a bare `Error` and have to
   * cast at the exact point the offline / forbidden / throttle distinction is made.
   */
  return queryOptions<InterviewPage, ApiError>({
    queryKey: queryKeys.interviews(viewerId, query),
    queryFn: () => fetchInterviews(query),
  });
}

/**
 * One interview, in the shape ADR 0006 allows **this** caller.
 *
 * A draft comes back in full only to its author — to an `Admin` and a `Recruiter` alike it is the
 * metadata shape — and a colleague's submitted interview is metadata only while this caller owes
 * feedback for the same person. It opens for the same id, with no other change, the moment their own
 * interview for that person is submitted.
 */
export async function fetchInterview(interviewId: number): Promise<InterviewRead> {
  return apiGet('/api/v1/interviews/{interview_id}/', { params: { interview_id: interviewId } });
}

/** Keyed on the viewer and inside the `interviews` family — see `queryKeys.interview` for both reasons. */
export function interviewQuery(viewerId: number, interviewId: number) {
  return queryOptions<InterviewRead, ApiError>({
    queryKey: queryKeys.interview(viewerId, interviewId),
    queryFn: () => fetchInterview(interviewId),
  });
}

/**
 * `InterviewUpdate` — the autosave body (ADR 0007, protection #4). Every field optional, **omittable
 * rather than nullable**: an explicit `null` is a 422 by design, and `PartialUpdateSchema`'s own reason
 * is worth knowing, because it is not tidiness — the `IntegrityError` it prevents would put the failing
 * row, **comment text included**, into the log through psycopg's `DETAIL: Failing row contains (…)`.
 */
export type InterviewDraft = BodyOf<'/api/v1/interviews/{interview_id}/', 'patch'>;

/** `ScoreUpdate` — `{question_id, rating?, comment?}`, an update of a score that already exists. */
export type ScoreDraft = NonNullable<InterviewDraft['scores']>[number];

/**
 * ⚠️ **The bound the autosave body actually has**, pinned against the document. ⚠️ It cited
 * `REORDER_MAX_QUESTIONS` as the precedent for the device, and that citation is **retracted**: the
 * constant and its route went with ADR 0018 (2026-08-11), so this is the only remaining instance of
 * the device rather than the second of two. `InterviewUpdate.scores` declares
 * `maxItems: 500`, so a longer list is a
 * 422 before anything is written, and `interviews.test.tsx` reads that number out of
 * `openapi.snapshot.json` and asserts this constant equals it.
 *
 * `draftChanges` respects it by **construction of the body**: it sends one entry per score whose rating
 * or comment differs from the last successful save, never the whole form, so the list is as long as what
 * one person typed between two saves rather than as long as the interview. What it deliberately does
 * **not** do is truncate: dropping a score to fit a bound would lose feedback silently, which is the one
 * failure ADR 0007 exists to prevent, and splitting one save into two requests is the *"save half the
 * form and believe it saved all of it"* that `InterviewUpdate`'s own docstring gives as the reason there
 * is no separate scores endpoint. An interview whose vacancy froze in more than this many questions
 * would therefore refuse a save that changed all of them at once — recorded as the bound TASK-083's
 * debounce keeps the client under, not worked around here.
 */
export const DRAFT_MAX_SCORES = 500;

/** One score as §6.6's form holds it: the two fields an interviewer can change, and no snapshot. */
export interface ScoreDraftFields {
  rating: Score['rating'];
  comment: Score['comment'];
}

/**
 * Everything an interviewer can change about a draft, keyed by `question_id` — the shape `draftChanges`
 * diffs. It is **the form's state, not a payload**: `question_text_snapshot` and `position` are absent
 * because neither is writable through any request body in this API, and a save never touches either.
 */
export interface DraftFields {
  // The form's own state, from the payload's declared set — `InterviewOut['overall_recommendation']`
  // rather than a locally named union, so the form cannot drift from what the API accepts.
  overall_recommendation: InterviewOut['overall_recommendation'];
  overall_comment: InterviewOut['overall_comment'];
  scores: Record<number, ScoreDraftFields>;
}

/** What the server currently holds, in the shape the form compares against. */
export function draftFieldsOf(interview: InterviewOut): DraftFields {
  const scores: Record<number, ScoreDraftFields> = {};
  for (const score of interview.scores) {
    scores[score.question_id] = { rating: score.rating, comment: score.comment };
  }

  return {
    overall_recommendation: interview.overall_recommendation,
    overall_comment: interview.overall_comment,
    scores,
  };
}

/**
 * **The autosave body: only what changed since the last successful save, and never an explicit `null`.**
 *
 * Two halves, and each is a decision rather than an optimisation:
 *
 *  * **Only what changed.** Every field the body carries is written and everything omitted keeps the
 *    value it had (`exclude_unset`), so a diff and a full form are equally correct to the server — but
 *    not equally safe. A full form is a client re-asserting text it may have loaded before another tab
 *    saved, and on a route bounded at `maxItems: 500` it is also the only way to approach that bound.
 *  * **Never an explicit `null`.** `PartialUpdateSchema` answers 422 for one, so a field with nothing to
 *    say is **absent**. Two consequences are stated rather than discovered: a **rating is set, not
 *    cleared** — an unanswered question is a score nobody rated, not one somebody blanked, and no screen
 *    offers *un-rate* — and the same holds for the verdict, which the API can neither clear (`null` is a
 *    422) nor set to blank (`''` is not one of the four), so §6.6 must not offer to undecide. A comment
 *    is a `string`, and `''` is a value rather than a `null`: clearing the text somebody typed is a
 *    lawful save and goes out as the empty string.
 *
 * **No length cap on a comment, and that is deliberate.** The document declares no `maxLength` on
 * `overall_comment` or on `ScoreUpdate.comment` (unlike `AddendumIn.text`, which declares 5000), so a
 * client-side limit would be a rule this product does not have, invented at the one place a person is
 * writing the thing the product exists to keep. `interviews.test.tsx` asserts both — that the document
 * declares none, and that a long comment goes out whole.
 */
export function draftChanges(saved: DraftFields, current: DraftFields): InterviewDraft {
  const changes: InterviewDraft = {};

  if (
    current.overall_recommendation !== null &&
    current.overall_recommendation !== saved.overall_recommendation
  ) {
    changes.overall_recommendation = current.overall_recommendation;
  }

  if (current.overall_comment !== saved.overall_comment) {
    changes.overall_comment = current.overall_comment;
  }

  const scores: ScoreDraft[] = [];
  for (const [key, fields] of Object.entries(current.scores)) {
    const questionId = Number(key);
    // A question the last successful save did not know about has nothing saved to compare with, so
    // everything on it counts as changed rather than as equal to a value nobody wrote.
    const before: ScoreDraftFields | undefined = saved.scores[questionId];
    const score: ScoreDraft = { question_id: questionId };

    if (fields.rating !== null && fields.rating !== before?.rating) score.rating = fields.rating;
    if (fields.comment !== before?.comment) score.comment = fields.comment;

    if ('rating' in score || 'comment' in score) scores.push(score);
  }

  if (scores.length > 0) changes.scores = scores;

  return changes;
}

/**
 * The autosave — **`PATCH`, on a debounce, author only** (an `Admin` and a `Recruiter` are refused with
 * 403: an interview is one person's opinion). Answers the full readable payload, whose `updated_at` is
 * the server's own save timestamp — §6.6's *"Saved 14:32"* — so the indicator reports what was saved
 * rather than when the request left.
 *
 * Its two 409s are the screen's to answer and not this layer's (TASK-083): *submitted already*, from
 * another tab (ADR 0008), and *the candidate's personal data was erased on request* (ADR 0009). Neither
 * writes anything, and neither may lose what is on screen.
 */
export async function saveInterviewDraft(
  interviewId: number,
  changes: InterviewDraft,
): Promise<InterviewOut> {
  return apiPatch('/api/v1/interviews/{interview_id}/', changes, {
    params: { interview_id: interviewId },
  });
}

/**
 * Submission — **the one irreversible act in the product**, and it sends **no body**.
 *
 * Everything it needs is already on the server: the draft has been there since the first keystroke (ADR
 * 0007), and a body would be a second place to write the very feedback being frozen. `submitted_at` is
 * in no request schema in this API.
 *
 * ⚠️ **Nothing in the type layer holds this to it.** The operation declares no `requestBody`, so `BodyOf`
 * widens instead of refusing and `apiPost(path, { submitted_at: … })` would **compile** (TASK-075). On
 * the product's single irreversible act the guard is therefore a test on the recorded request —
 * `interviews.test.tsx` asserts axios was handed `undefined`, not `{}` and not `null` — and there is no
 * second one.
 *
 * There is no way back: no endpoint clears `submitted_at`, there is no un-submit and no `DELETE`, and the
 * Django admin is read-only. A changed opinion is an addendum (ADR 0008).
 */
export async function submitInterview(interviewId: number): Promise<InterviewOut> {
  return apiPost('/api/v1/interviews/{interview_id}/submit/', undefined, {
    params: { interview_id: interviewId },
  });
}

/**
 * `SubmissionProblem` — **one reason §6.6's completeness gate refused**, and the entry type of the 422
 * body of both write routes on this surface (`SubmissionRefused` and `ScoresRefused` declare the same
 * `detail: SubmissionProblem[]`, deliberately, so a client parses one shape and not two).
 *
 * ⚠️ **`msg` is the server's sentence and no screen may render it** (§8, §11) — it is an English string
 * chosen on the backend, and a client that renders it hands its own copy over to a repository it does not
 * control. What the client reads is `type` (machine-readable), `loc` and `question_id`, which is how the
 * offending field is found: `pages/Interview/submissionProblems.ts` maps the three to a field of §6.6's
 * form and owns the wording. The type is exported here rather than derived at the page, because reading a
 * refusal body's shape off the document is this layer's job (`.claude/rules/frontend.md`: types come from
 * the OpenAPI schema, never hand-written).
 */
export type SubmissionProblem = components['schemas']['SubmissionProblem'];

/**
 * The kinds of refusal the API declares (backend TASK-106) — `comment_required`, `missing` and the
 * autosave's `value_error`.
 *
 * ⚠️ **Generated, and the `Record` is what keeps the runtime list and the type in step.** §6.6's mapping
 * of a refusal to a field re-declared these strings until the server declared them; the mapping needs a
 * *runtime* membership test, so a `Record<ApiProblemType, true>` is the one shape that fails `tsc` when
 * a kind is added or removed on the server rather than falling through to `unknown` in a browser. Same
 * device as `vocabularies.ts`'s two enums, for the same reason.
 */
export type ApiProblemType = SubmissionProblem['type'];

const API_PROBLEM_TYPE: Record<ApiProblemType, true> = {
  comment_required: true,
  missing: true,
  value_error: true,
};

export const API_PROBLEM_TYPES = Object.keys(API_PROBLEM_TYPE) as ApiProblemType[];

/**
 * `AddendumIn` — **`text` and nothing else**: no rating and no recommendation, so no average, no counter
 * and no *latest recommendation* moves because somebody changed their mind (ADR 0008, decision 2).
 */
export type NewAddendum = BodyOf<'/api/v1/interviews/{interview_id}/addenda/', 'post'>;

/**
 * A change of mind, recorded **beside** the frozen feedback and never over it — the act ADR 0008 names
 * as the replacement for editing, and the reason §6.7 has no edit button (protection #5).
 *
 * **Author only**, exactly as the autosave is: an `Admin`'s or a `Recruiter`'s addendum would annotate
 * another person's opinion inside the record of it. A draft is a 409 — there is nothing immutable to
 * amend yet — and an anonymised candidate is the other 409 (ADR 0009). An addendum cannot itself be
 * corrected; a typo is answered by another addendum, and several on one interview are ordinary.
 */
export const writeAddendum = (interviewId: number, addendum: NewAddendum) =>
  apiPost('/api/v1/interviews/{interview_id}/addenda/', addendum, {
    params: { interview_id: interviewId },
  });

/**
 * `InterviewIn` — `{interviewer_id, scheduled_at?}`. No `scores` (the bank is frozen in by the server,
 * ADR 0004), no `submitted_at`, no `status` and no `tenant` (`.claude/rules/tenancy.md`).
 */
export type NewInterview = BodyOf<'/api/v1/considerations/{consideration_id}/interviews/', 'post'>;

/** `InterviewCreatedOut` — metadata plus `score_count` and the consideration's status after the move. */
export type ScheduledInterview = ResponseOf<
  '/api/v1/considerations/{consideration_id}/interviews/',
  'post'
>;

/**
 * Scheduling — **Admin or Recruiter** (`(401, 403, 201, 201, 403, 403)`: an `Interviewer` is 403 here
 * even after TASK-029 widened their *read* access, because scheduling is a recruiting act), and the
 * moment ADR 0004 freezes the bank in: one `Score` per
 * *active* question of the consideration's vacancy, in bank order, with the wording and the position
 * copied now. Editing, reordering or archiving a question afterwards leaves this interview as it was.
 *
 * It also moves the consideration to `INTERVIEWING` **when, and only when, it is `NEW`** — through the
 * log, as a consequence rather than as a field (ADR 0010) — which is why `consideration_status` is on the
 * answer: the client that scheduled it needs to know without a second request.
 *
 * ⚠️ **`interviewer_id` is a user id and there is no user-lookup route in the whole API**, so its one
 * caller sends the caller's own id and says so in a sentence (`PersonCard/ScheduleInterviewDialog`,
 * TASK-081): the person scheduling is the person conducting. Assigning a colleague — without which an
 * account in the `Interviewer` group can be given nothing to do through the interface at all — is a
 * product-level gap recorded in the Backlog, not something to work around here.
 *
 * **409 — the candidate's personal data was erased on request** (ADR 0009), and nothing is written: no
 * interview, no score, no status move and no log row.
 */
export const scheduleInterview = (considerationId: number, interview: NewInterview) =>
  apiPost('/api/v1/considerations/{consideration_id}/interviews/', interview, {
    params: { consideration_id: considerationId },
  });
